// Demo mode (investor demos without a demo environment).
//
// Resolves the flag ONCE at module load, before React renders, so
// ``isDemoMode()`` is a stable constant for the whole page lifetime: SPA
// navigation and refresh keep it; only ``?demo=0``, clearing site storage, or
// a different browser/incognito resets it. See docs/Y2026-Q2/design-demo-mode.md.
//
// Toggle: visit ``…?demo=1`` to enter, ``…?demo=0`` to exit. The param is read,
// persisted to localStorage, then stripped from the URL so it stays clean and
// SPA navigation doesn't carry it around.

const STORAGE_KEY = "irAgentsDemoMode";

const resolveDemoMode = (): boolean => {
  // Guarded: a privacy-mode browser can throw on localStorage access, and a
  // non-browser context (tests) has no window. Demo mode is a display-only
  // convenience, so degrade to "off" rather than crash the app.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("demo")) {
      const raw = (params.get("demo") ?? "").toLowerCase();
      const on =
        raw === "" || raw === "1" || raw === "true" || raw === "on" || raw === "yes";
      if (on) window.localStorage.setItem(STORAGE_KEY, "1");
      else window.localStorage.removeItem(STORAGE_KEY);
      params.delete("demo");
      const qs = params.toString();
      const cleanUrl =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState(window.history.state, "", cleanUrl);
    }
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const DEMO_MODE = resolveDemoMode();

export const isDemoMode = (): boolean => DEMO_MODE;

// Thrown by the API shim when a blocked call is attempted in demo mode. Demo
// mode lets you run the real flows (upload, run agents, converse) - the backend
// always works from real data, so those never corrupt anything. Only the
// genuinely dangerous paths are blocked (see DEMO_BLOCKED_PATHS +
// docs/Y2026-Q2/design-demo-mode.md).
export class DemoModeError extends Error {
  readonly path: string;
  constructor(path: string) {
    super("This action is disabled in demo mode.");
    this.name = "DemoModeError";
    this.path = path;
  }
}

// Block-list of backend paths NOT permitted in demo mode. Everything else is
// allowed so the live demo can upload files and run agents. Only three classes are blocked:
//
// 1. Anonymised write-back (CORRUPTION): a screen hydrated from an anonymised
//    GET that writes its fields back would persist the FAKE values over the
//    real ones.
// 2. Destructive ops on real data (delete / merge of projects, files,
//    global reference data).
// 3. Raw-content exposure: endpoints that return un-anonymisable document
//    bytes / text, which would leak real PII on a demo screen.
//
// Create / upload / agent-run / procurement paths are
// deliberately NOT here: the backend uses real values (anonymisation is
// FE-display-only), so they create real data without corrupting anything.
export const DEMO_BLOCKED_PATHS: ReadonlySet<string> = new Set([
  // 1. anonymised write-back -> corruption
  "/projects/put",
  // 2. destructive
  "/projects/delete",
  "/projects/bulk_delete",
  "/projects/merge",
  "/uploads/delete",
  "/uploads/dismiss",
  "/global-data/delete",
  // 2b. config removal (reversible, but no reason to let a demo run it):
  // disconnecting a sync or excluding a file from a real project or global data.
  "/projects/drive/disconnect",
  "/projects/drive/use-my-account",
  "/projects/drive/exclude-file",
  "/projects/drive/remove-root",
  "/projects/drive/untrack-file",
  "/projects/gmail/exclude-thread",
  "/projects/gmail/reinstate-thread",
  "/google/disconnect",
  "/projects/mailboxes/disconnect",
  "/global-data/drive/disconnect",
  "/global-data/drive/file/remove",
  "/slack/unsubscribe",
  // 3. raw-content exposure (real document bytes / text)
  "/uploads/download_url",
  "/uploads/extracted-text",
  // Real correspondents' addresses, for a draft in a real mailbox.
  "/uploads/reply_context",
  "/global-data/download_url",
  // Slack messages verbatim: real names, emails and conversation.
  "/slack/day-document",
]);
